import { Card, CardContent } from '../ui/card';
import { Phone, TrendingUp, AlertTriangle, Target, Zap, ListTodo } from 'lucide-react';
import { getWinProbability } from './utils';

const Delta = ({ value }) => {
  if (value === null) return null;
  return (
    <span className={`text-[10px] font-semibold ${value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
      {value >= 0 ? '\u2191' : '\u2193'}{Math.abs(value)}%
    </span>
  );
};

const DeltaInverse = ({ value }) => {
  if (value === null) return null;
  return (
    <span className={`text-[10px] font-semibold ${value <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
      {value >= 0 ? '\u2191' : '\u2193'}{Math.abs(value)}%
    </span>
  );
};

const calcDelta = (curr, prevVal) => {
  if (prevVal === undefined || prevVal === null || prevVal === 0) return null;
  const pct = Math.round(((curr - prevVal) / prevVal) * 100);
  if (pct === 0) return null;
  return pct;
};

/**
 * 6-card KPI strip for the Manager dashboard.
 * `metrics` shape: { total_calls, avg_deal_score, hot_deal_count, sql_count, high_risk_count, prev: {...} }
 * `taskSummary` shape: { todo, in_progress, overdue }
 */
export default function KpiGrid({ metrics, taskSummary, onTasksClick }) {
  const prev = metrics?.prev;
  const wp = getWinProbability(metrics?.avg_deal_score || 0);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      <Card className="bg-card border-border/50 hover:border-primary/30 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total Calls</p>
              <div className="flex items-center gap-1.5">
                <p className="font-heading text-2xl font-bold text-foreground">{metrics?.total_calls || 0}</p>
                <Delta value={calcDelta(metrics?.total_calls || 0, prev?.total_calls)} />
              </div>
            </div>
            <Phone className="w-5 h-5 text-primary" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50 hover:border-primary/30 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Avg Win %</p>
              <div className="flex items-center gap-1.5">
                <p className={`font-heading text-2xl font-bold ${wp.color}`}>{wp.pct}%</p>
                <Delta value={calcDelta(metrics?.avg_deal_score || 0, prev?.avg_deal_score)} />
              </div>
            </div>
            <TrendingUp className="w-5 h-5 text-success" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50 hover:border-primary/30 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Hot Deals</p>
              <div className="flex items-center gap-1.5">
                <p className="font-heading text-2xl font-bold text-orange-600">{metrics?.hot_deal_count || 0}</p>
                <Delta value={calcDelta(metrics?.hot_deal_count || 0, prev?.hot_deal_count)} />
              </div>
            </div>
            <Zap className="w-5 h-5 text-orange-600" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50 hover:border-primary/30 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">SQLs</p>
              <div className="flex items-center gap-1.5">
                <p className="font-heading text-2xl font-bold text-success">{metrics?.sql_count || 0}</p>
                <Delta value={calcDelta(metrics?.sql_count || 0, prev?.sql_count)} />
              </div>
            </div>
            <Target className="w-5 h-5 text-success" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50 hover:border-primary/30 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">High Risk</p>
              <div className="flex items-center gap-1.5">
                <p className="font-heading text-2xl font-bold text-warning">{metrics?.high_risk_count || 0}</p>
                <DeltaInverse value={calcDelta(metrics?.high_risk_count || 0, prev?.high_risk_count)} />
              </div>
            </div>
            <AlertTriangle className="w-5 h-5 text-warning" />
          </div>
        </CardContent>
      </Card>

      <Card
        className="bg-card border-border/50 hover:border-primary/30 transition-colors cursor-pointer"
        onClick={onTasksClick}
        data-testid="pending-tasks-card"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Pending Tasks</p>
              <div className="flex items-center gap-1.5">
                <p className={`font-heading text-2xl font-bold ${
                  (taskSummary?.overdue || 0) > 0 ? 'text-destructive' : 'text-foreground'
                }`}>
                  {(taskSummary?.todo || 0) + (taskSummary?.in_progress || 0)}
                </p>
                {(taskSummary?.overdue || 0) > 0 && (
                  <span className="text-[10px] font-semibold text-red-600">{taskSummary.overdue} overdue</span>
                )}
              </div>
            </div>
            <ListTodo className="w-5 h-5 text-primary" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
